import { DateTime } from 'luxon';
import { cloneDeep } from 'lodash';
import { ToastController } from 'ionic-angular';
import {DateService} from '../../../../services/date/date.service';
import {JackingEventRowEditor, JackingFormInstanceEditor} from '../../../../models/jacking-v2/jacking.models';
import {PtLuxonDate} from '../../../../models/projects/date.models';


export interface JackingEventsTimeEngineOptions {
  activitySpansMultipleDays: boolean;
  currentDailyLogDate?: PtLuxonDate;
  onViewLegsFreeRequested?: () => void;
}

export interface JackingRowTimeUiState {
  selectedLocalDateIso: string;
  userPickedDate: boolean;
}

export class JackingEventsTimeEngine {
  private static readonly ACTION_TOAST_DURATION_MS = 4000;
  private selectedLocalDateIsoByRowId = new Map<number, string>();
  private userPickedDateByRowId = new Map<number, boolean>();

  constructor(
    private dateService: DateService,
    private toastCtrl: ToastController,
    private opts: JackingEventsTimeEngineOptions
  ) {}

  public updateOptions(opts: Partial<JackingEventsTimeEngineOptions>): void {
    this.opts = {
      ...this.opts,
      ...opts
    };
  }

  private async presentEndDateUpdatedToast(): Promise<void> {
    await this.presentActionToast('End Date Updated', 'View End Date');
  }

  private async presentLegsFreeUpdatedToast(): Promise<void> {
    await this.presentActionToast('Legs Free Time Updated', 'View Legs Free');
  }

  private async presentActionToast(message: string, closeButtonText: string): Promise<void> {
    const toast = await this.toastCtrl.create({
      message,
      position: 'bottom',
      showCloseButton: true,
      closeButtonText
    });

    let dismissStarted = false;
    let autoDismissTimer: number | null = window.setTimeout(() => {
      if (!dismissStarted) {
        dismissStarted = true;
        toast.dismiss(undefined, 'timeout');
      }
    }, JackingEventsTimeEngine.ACTION_TOAST_DURATION_MS);

    toast.onWillDismiss(() => {
      dismissStarted = true;
    });

    toast.onDidDismiss((_data, role) => {
      if (autoDismissTimer !== null) {
        window.clearTimeout(autoDismissTimer);
        autoDismissTimer = null;
      }

      if (role === 'close' && this.opts.onViewLegsFreeRequested) {
        this.opts.onViewLegsFreeRequested();
      }
    });

    await toast.present();
  }

  public hydrateRow(row: JackingEventRowEditor): void {
    // ensure eventDate is a real PtLuxonDate instance (API returns plain objects)
    row.eventDate = row.eventDate ? PtLuxonDate.assignPtLuxonDate(row.eventDate as any) : undefined;

    const dt = row.eventDate?.dateTime || null;

    // keep both in sync
    row.timeToShow = dt ? dt.toFormat('HH:mm') : '';

    if (!row._mask) {
      row._mask = [
        new RegExp('[0-2]'),
        new RegExp('\\d'),
        ':',
        new RegExp('[0-5]'),
        new RegExp('\\d')
      ];
    }
    this.updateMask(row._mask, row.timeToShow);

    // cache selected local ISO date for multi-day picker defaulting
    const iso = dt ? (dt.toISODate() || '') : '';
    this.selectedLocalDateIsoByRowId.set(row.id, iso);

    // don't mark as "picked" during hydration
    this.userPickedDateByRowId.set(row.id, false);
  }

  public getRowMask(row: JackingEventRowEditor): any[] {
    if (!row._mask) {
      row._mask = [
        new RegExp('[0-2]'),
        new RegExp('\\d'),
        ':',
        new RegExp('[0-5]'),
        new RegExp('\\d')
      ];
    }
    return row._mask;
  }

  public setUserPickedDate(row: JackingEventRowEditor, picked: boolean): void {
    this.userPickedDateByRowId.set(row.id, picked);
  }

  public getSelectedLocalDateIso(row: JackingEventRowEditor): string {
    return this.selectedLocalDateIsoByRowId.get(row.id) || '';
  }

  public setSelectedLocalDateIso(row: JackingEventRowEditor, iso: string): void {
    this.selectedLocalDateIsoByRowId.set(row.id, iso || '');
  }

  public async onDateChanged(
    editor: JackingFormInstanceEditor,
    orderedRows: JackingEventRowEditor[],
    row: JackingEventRowEditor,
    dt: DateTime
  ): Promise<void> {
    this.setSelectedLocalDateIso(row, dt?.toISODate?.() || '');
    this.setUserPickedDate(row, true);

    if (!dt) {
      return;
    }

    const existing = row.eventDate ? PtLuxonDate.assignPtLuxonDate(row.eventDate as any) : null;

    const timeToShow = (row.timeToShow || '').trim();
    const [hoursText, minutesText] = timeToShow.split(':');
    const hasCompleteTime = /^\d{2}:\d{2}$/.test(timeToShow);
    const pickerDateOnly = dt.set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0
    });

    let finalDateTime = existing?.dateTime
      ? existing.dateTime.set({
        year: pickerDateOnly.year,
        month: pickerDateOnly.month,
        day: pickerDateOnly.day
      })
      : pickerDateOnly;

    if (hasCompleteTime) {
      finalDateTime = finalDateTime.set({
        hour: Number(hoursText),
        minute: Number(minutesText),
        second: 0,
        millisecond: 0
      });
    }

    row.eventDate = existing ? existing : PtLuxonDate.assignPtLuxonDate({ dateTime: finalDateTime } as PtLuxonDate);
    row.eventDate.dateTime = finalDateTime;

    row.eventDateOffset = row.eventDate?.dateTime?.offset / 60;

    if (this.opts.activitySpansMultipleDays) {
      this.setSelectedLocalDateIso(row, row.eventDate.dateTime?.toISODate() || '');
    }

    await this.bumpEditorEndIfNeeded(editor, row.eventDate.dateTime, row, false);
    this.applyOperationStartLink(editor, row);
  }

  public async onTimeTextChanged(
    editor: JackingFormInstanceEditor,
    orderedRows: JackingEventRowEditor[],
    row: JackingEventRowEditor,
    timeToShow: string
  ): Promise<void> {
    const t = (timeToShow || '').trim();
    row.timeToShow = t;

    this.updateMask(this.getRowMask(row), t);
    this.setUserPickedDate(row, false);

    await this.onTimeAltered(editor, orderedRows, row, { dateChanged: false });
  }

  public async onOffsetSelected(
    editor: JackingFormInstanceEditor,
    orderedRows: JackingEventRowEditor[],
    row: JackingEventRowEditor,
    ptLocal: PtLuxonDate
  ): Promise<void> {
    if (!ptLocal?.dateTime) {
      return;
    }

    const original = row.eventDate ? PtLuxonDate.assignPtLuxonDate(cloneDeep(row.eventDate as any)) : null;

    row.eventDate = PtLuxonDate.assignPtLuxonDate(ptLocal as any);
    row.eventDateOffset = row.eventDate.dateTime.offset / 60;

    if (this.opts.activitySpansMultipleDays) {
      this.setSelectedLocalDateIso(row, row.eventDate.dateTime.toISODate() || '');
    }

    // offset selection is NOT an explicit date change
    const bumpedToNextDay = await this.applyNextDayRule(editor, orderedRows, row, original);

    // v1 behaviour: keep end date covering entered events
    await this.bumpEditorEndIfNeeded(editor, row.eventDate.dateTime, row, bumpedToNextDay);

    // keep linked start time up to date
    this.applyOperationStartLink(editor, row);
  }

  public async onTimeAltered(
    editor: JackingFormInstanceEditor,
    orderedRows: JackingEventRowEditor[],
    row: JackingEventRowEditor,
    opts: { dateChanged: boolean }
  ): Promise<void> {
    const timeToShow = (row.timeToShow || '').trim();

    // cleared
    if (!timeToShow) {
      row.eventDate = undefined;
      row.eventDateOffset = null;
      row.timeConfirmed = false;

      // leave selectedLocalDateIso as-is (it helps date picker defaulting)
      this.applyOperationStartLink(editor, row);
      return;
    }

    const original = row.eventDate ? PtLuxonDate.assignPtLuxonDate(cloneDeep(row.eventDate as any)) : null;

    const baseDate = this.getBaseDateForEntry(editor, orderedRows, row);
    const ptLocal = await this.buildLocalPtAllowAmbiguous(timeToShow, baseDate);

    if (!ptLocal) {
      return;
    }

    row.eventDate = ptLocal;
    row.eventDateOffset = ptLocal.dateTime.offset / 60;

    if (this.opts.activitySpansMultipleDays) {
      this.setSelectedLocalDateIso(row, ptLocal.dateTime.toISODate() || '');
    }

    // If offset still needs choosing, stop here so the offset picker can render.
    if (ptLocal.offsetNull()) {
      this.applyOperationStartLink(editor, row);
      return;
    }

    // v1 behaviour: only auto-bump if user didn't explicitly choose the date (for multi-day mode)
    let bumpedToNextDay = false;
    const userExplicitlyChangedDate = !!opts.dateChanged && this.opts.activitySpansMultipleDays;
    if (!userExplicitlyChangedDate) {
      bumpedToNextDay = await this.applyNextDayRule(editor, orderedRows, row, original);
    }

    // v1 behaviour: keep end date covering entered events
    await this.bumpEditorEndIfNeeded(editor, row.eventDate.dateTime, row, bumpedToNextDay);

    // linked operation start
    this.applyOperationStartLink(editor, row);
  }

  // ---------------------------
  // Core rules / helpers
  // ---------------------------

  private updateMask(mask: any[], inputTime: string): void {
    if (!inputTime) {
      mask[1] = new RegExp('\\d');
      return;
    }
    mask[1] = inputTime.charAt(0) === '2' ? new RegExp('[0-3]') : new RegExp('\\d');
  }

  private getBaseDateForEntry(
    editor: JackingFormInstanceEditor,
    orderedRows: JackingEventRowEditor[],
    row: JackingEventRowEditor
  ): DateTime {
    const startUtc = editor?.startDate?.dateTime || DateTime.utc();
    const startOffsetMinutes = editor?.startOffset || 0;

    // start local day iso
    const startLocal = startUtc.plus({ minutes: startOffsetMinutes || 0 });
    let localIso = startLocal.toISODate() || '';

    if (this.opts.activitySpansMultipleDays) {
      // if user picked a date previously, prefer it
      const selected = (this.getSelectedLocalDateIso(row) || '').trim();
      if (selected) {
        localIso = selected;
      } else {
        // otherwise default to the latest previous entered event day
        const prevIso = this.getPreviousEnteredLocalIsoDate(orderedRows, row);
        if (prevIso) {
          localIso = prevIso;
        }
      }

      const currentDailyIso = this.opts.currentDailyLogDate?.dateTime?.toISODate?.() || '';
      if (currentDailyIso && localIso && currentDailyIso > localIso) {
        localIso = currentDailyIso;
      }
    }

    const [y, m, d] = localIso.split('-').map(n => Number(n));
    return DateTime.fromObject({ year: y, month: m, day: d, hour: 0, minute: 0 }, { zone: 'utc' });
  }

  private getPreviousEnteredLocalIsoDate(orderedRows: JackingEventRowEditor[], row: JackingEventRowEditor): string | null {
    const idx = orderedRows.findIndex(r => r === row);
    if (idx <= 0) {
      return null;
    }

    for (let i = idx - 1; i >= 0; i--) {
      const prev = orderedRows[i];

      if (!prev?.requiresDateTime) {
        continue;
      }

      prev.eventDate = prev.eventDate ? PtLuxonDate.assignPtLuxonDate(prev.eventDate as any) : undefined;

      const dt = prev.eventDate?.dateTime;
      if (!dt) {
        continue;
      }

      if (prev.eventDate?.offsetNull && prev.eventDate.offsetNull()) {
        continue;
      }

      return dt.toISODate() || null;
    }

    return null;
  }

  private async buildLocalPtAllowAmbiguous(timeToShow: string, baseDateUtcMidnight: DateTime): Promise<PtLuxonDate | null> {
    const t = (timeToShow || '').trim();
    if (!t) {
      return null;
    }

    const allowed = await this.dateService.timeAllowed(t, baseDateUtcMidnight);
    if (!allowed) {
      return null;
    }

    const raw = await this.dateService.setPtLuxonDateFromUserEnteredString(t, baseDateUtcMidnight);
    const pt = PtLuxonDate.assignPtLuxonDate(raw as any);

    if (!pt?.dateTime) {
      return null;
    }

    if (pt.isLostInTime?.result) {
      return null;
    }

    // IMPORTANT: do NOT return null for offsetNull() here — caller must keep it to show offset picker
    return pt;
  }

  private async applyNextDayRule(
    editor: JackingFormInstanceEditor,
    orderedRows: JackingEventRowEditor[],
    row: JackingEventRowEditor,
    originalTimeCopy: PtLuxonDate | null
  ): Promise<boolean> {
    row.eventDate = row.eventDate ? PtLuxonDate.assignPtLuxonDate(row.eventDate as any) : undefined;

    if (!row.eventDate?.dateTime) {
      return false;
    }

    if (row.linkTargetType === 'operationStart') {
      return false;
    }

    // avoid bumping again if this edit already moved day previously (v1 logic)
    if (originalTimeCopy?.dateTime) {
      const sameDay = await this.dateService.compareDayOffsetsPreApplied(row.eventDate.dateTime, originalTimeCopy.dateTime);
      if (!sameDay) {
        return false;
      }
    }

    const idx = orderedRows.findIndex(r => r === row);
    let previousEnteredRowFound = false;

    if (idx > 0) {
      for (let i = idx - 1; i >= 0; i--) {
        const prev = orderedRows[i];

        if (!prev?.requiresDateTime) {
          continue;
        }

        prev.eventDate = prev.eventDate ? PtLuxonDate.assignPtLuxonDate(prev.eventDate as any) : undefined;

        if (!prev.eventDate?.dateTime) {
          continue;
        }

        if (prev.eventDate.offsetNull && prev.eventDate.offsetNull()) {
          continue;
        }

        const prevTimeToShow = (prev.timeToShow || '').trim();
        if (prevTimeToShow) {
          const allowed = await this.dateService.timeAllowed(prevTimeToShow, prev.eventDate.getDate());
          if (!allowed) {
            continue;
          }
        }

        previousEnteredRowFound = true;

        if (this.dateService.isFirstDateLargerOffsetsPreApplied(prev.eventDate.dateTime, row.eventDate.dateTime)) {
          const bumpedRaw = await this.dateService.incrementPtLuxonDateFromUserEnteredString(
            (row.timeToShow || '').trim(),
            row.eventDate.dateTime
          );

          row.eventDate = PtLuxonDate.assignPtLuxonDate(bumpedRaw as any);
          row.eventDateOffset = row.eventDate.dateTime.offset / 60;

          if (this.opts.activitySpansMultipleDays) {
            this.setSelectedLocalDateIso(row, row.eventDate.dateTime.toISODate() || '');
          }

          return true;
        }

        break;
      }
    }

    if (!previousEnteredRowFound) {
      const baselineDt = this.getFormBaselineDateTime(editor);

      if (baselineDt && this.dateService.isFirstDateLargerOffsetsPreApplied(baselineDt, row.eventDate.dateTime)) {
        const bumpedRaw = await this.dateService.incrementPtLuxonDateFromUserEnteredString(
          (row.timeToShow || '').trim(),
          row.eventDate.dateTime
        );

        row.eventDate = PtLuxonDate.assignPtLuxonDate(bumpedRaw as any);
        row.eventDateOffset = row.eventDate.dateTime.offset / 60;

        if (this.opts.activitySpansMultipleDays) {
          this.setSelectedLocalDateIso(row, row.eventDate.dateTime.toISODate() || '');
        }

        return true;
      }
    }

    return false;
  }

  private getFormBaselineDateTime(editor: JackingFormInstanceEditor): DateTime | null {
    const startDt = editor?.startDate?.dateTime || null;
    const endDt = editor?.endDate?.dateTime || null;

    if (startDt && endDt) {
      return this.dateService.isFirstDateLargerOffsetsPreApplied(endDt, startDt) ? endDt : startDt;
    }

    return endDt || startDt || null;
  }

  public async bumpEditorEndIfNeeded(
    editor: JackingFormInstanceEditor,
    candidateDt: DateTime,
    sourceRow?: JackingEventRowEditor,
    bumpedToNextDay: boolean = false
  ): Promise<void> {
    if (!editor?.endDate || !candidateDt) {
      return;
    }

    // Ensure endDate is a real PtLuxonDate instance
    editor.endDate = PtLuxonDate.assignPtLuxonDate(editor.endDate);

    if (!editor.endDate?.dateTime || !candidateDt) {
      return;
    }

    if (
      (editor.formTypeId ?? '').toLowerCase() === 'departure' &&
      sourceRow?.canBeAfterEndTime
    ) {
      return;
    }

    const formType = (editor.formTypeId ?? '').toLowerCase();
    const isDeparture = formType === 'departure';

    if (!this.dateService.isFirstDateLargerOffsetsPreApplied(candidateDt, editor.endDate.dateTime)) {
      return;
    }

    this.applyEditorEndDate(editor, candidateDt);

    if (isDeparture) {
      this.syncLegsFreeField(editor);
    }

    await this.presentLinkedEndTimeUpdatedToast(formType);
  }

  private applyEditorEndDate(editor: JackingFormInstanceEditor, dateTime: DateTime): void {
    editor.endDate = PtLuxonDate.assignPtLuxonDate(editor.endDate);
    editor.endDate.dateTime = dateTime;

    const dtLocal = editor.endDate.dateTime;
    const offsetHours = dtLocal.offset / 60;
    const offsetLabel = `(${offsetHours >= 0 ? '+' : ''}${offsetHours})`;

    editor.dataByKey = editor.dataByKey || {};
    editor.dataByKey.EndDate = `${dtLocal.toFormat('dd/LL/yyyy, HH:mm')} ${offsetLabel}`;
    editor.endOffset = editor.endDate.dateTime.offset / 60;
  }

  private syncLegsFreeField(editor: JackingFormInstanceEditor): void {
    const legsFreeId = this.findFieldTemplateIdByLabel(editor, 'Legs Free Time');
    if (legsFreeId == null) {
      return;
    }

    editor.valuesByFieldId = editor.valuesByFieldId || {};
    editor.valuesByFieldId[legsFreeId] = editor.valuesByFieldId[legsFreeId] || { fieldTemplateId: legsFreeId };
    editor.valuesByFieldId[legsFreeId].fieldTemplateId = legsFreeId;
    editor.valuesByFieldId[legsFreeId].dateTimeValue = editor.endDate;
    editor.valuesByFieldId[legsFreeId].timeZoneOffsetValue = editor.endOffset;
    editor.valuesByFieldId[legsFreeId].boolValue = false;
  }

  private async presentLinkedEndTimeUpdatedToast(formType: string): Promise<void> {
    if (formType === 'arrival') {
      await this.presentEndDateUpdatedToast();
      return;
    }

    await this.presentLegsFreeUpdatedToast();
  }

  private applyOperationStartLink(editor: JackingFormInstanceEditor, row: JackingEventRowEditor): void {
    if (!editor || !row || !row.eventDate) {
      return;
    }

    row.eventDate = PtLuxonDate.assignPtLuxonDate(row.eventDate as any);

    if (row.linkTargetType === 'operationStart' && row.eventDate?.dateTime) {
      editor.startDate = row.eventDate;

      const dtLocal = row.eventDate.dateTime;
      const offsetHours = dtLocal.offset / 60;
      const offsetLabel = `(${offsetHours >= 0 ? '+' : ''}${offsetHours})`;

      editor.dataByKey = editor.dataByKey || {};
      editor.dataByKey.StartDate = `${dtLocal.toFormat('dd/LL/yyyy, HH:mm')} ${offsetLabel}`;
    }
  }

  private findFieldTemplateIdByLabel(editor: JackingFormInstanceEditor, label: string): number | null {
    const sections = editor?.template?.sections ?? [];
    for (const s of sections) {
      const groups = s?.groups ?? [];
      for (const g of groups) {
        if (!g?.usesFieldTemplates) { continue; }
        const fields = g?.fields ?? [];
        const match = fields.find(f => (f?.label ?? '').trim().toLowerCase() === label.trim().toLowerCase());
        if (match?.id != null) { return match.id; }
      }
    }
    return null;
  }

}



// WEBPACK FOOTER //
// ./src/pages/jacking-activities/v2/helpers/jacking-events-time-engine.helper.ts